import { CalendarDate, getCalendarDays, isToday, isCurrentMonth, isEventPast } from '../../utils/calendar';
import { CalendarEvent } from '../../types/calendar';
import { getDesignTokens } from '../../utils/designTokens';
import EventCard from './EventCard';

interface MonthViewProps {
  year: number;
  month: number;
  events: CalendarEvent[];
  onDateClick?: (date: Date) => void;
  onEventClick?: (event: CalendarEvent) => void;
  onNavigateToDay?: (date: Date) => void;
  isDarkMode?: boolean;
}

export default function MonthView({
  year,
  month,
  events,
  onDateClick,
  onEventClick,
  onNavigateToDay,
  isDarkMode = false,
}: MonthViewProps) {
  const calendarDays = getCalendarDays(year, month);
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Group events by date - include events that span multiple days
  const eventsByDate = new Map<string, CalendarEvent[]>();
  events.forEach((event) => {
    const startDate = new Date(event.startTime);
    const endDate = new Date(event.endTime);
    
    // Add event to all dates it spans
    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      const dateKey = `${currentDate.getFullYear()}-${currentDate.getMonth()}-${currentDate.getDate()}`;
      if (!eventsByDate.has(dateKey)) {
        eventsByDate.set(dateKey, []);
      }
      // Only add event once per date (avoid duplicates)
      if (!eventsByDate.get(dateKey)!.some(e => e.id === event.id)) {
        eventsByDate.get(dateKey)!.push(event);
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }
  });

  const getEventsForDate = (date: CalendarDate): CalendarEvent[] => {
    const dateKey = `${date.year}-${date.month}-${date.day}`;
    const dateEvents = eventsByDate.get(dateKey) || [];
    
    // Sort events: upcoming first, past last
    return dateEvents.sort((a, b) => {
      const aIsPast = isEventPast(a);
      const bIsPast = isEventPast(b);
      if (aIsPast !== bIsPast) {
        return aIsPast ? 1 : -1; // Upcoming events first
      }
      // If both are past or both are upcoming, sort by start time
      const aStart = a.startTime instanceof Date ? a.startTime : new Date(a.startTime);
      const bStart = b.startTime instanceof Date ? b.startTime : new Date(b.startTime);
      return aStart.getTime() - bStart.getTime();
    });
  };

  const tokens = getDesignTokens(isDarkMode);
  const theme = {
    bg: tokens.card.bg,
    border: isDarkMode ? 'border-[rgba(107,114,128,0.3)]' : 'border-[rgba(217,119,111,0.25)]', // Peachy warm tone
    text: tokens.text.primary,
    textMuted: tokens.text.muted,
    today: isDarkMode 
      ? 'bg-[rgba(96,165,250,0.2)] border-[rgba(96,165,250,0.5)]' 
      : 'bg-[rgba(217,119,111,0.15)] border-[rgba(217,119,111,0.4)]',
    hasEvent: isDarkMode
      ? 'bg-gradient-to-br from-[rgba(96,165,250,0.25)] via-[rgba(168,85,247,0.2)] to-[rgba(96,165,250,0.25)] border-[rgba(96,165,250,0.4)]'
      : 'bg-gradient-to-br from-[rgba(217,119,111,0.15)] via-[rgba(192,132,124,0.12)] to-[rgba(217,119,111,0.15)] border-[rgba(217,119,111,0.3)]',
    otherMonth: isDarkMode
      ? `${tokens.bg.secondary} ${tokens.text.muted} border-[rgba(107,114,128,0.2)]`
      : `${tokens.bg.secondary} ${tokens.text.muted} border-[rgba(217,119,111,0.2)]`,
      };

  return (
    <div className="w-full h-full flex flex-col overflow-hidden">
      {/* Week day headers - fixed at top */}
      <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-1 sm:mb-2 flex-shrink-0 sticky top-0 z-10 bg-inherit">
        {weekDays.map((day) => (
          <div
            key={day}
            className={`text-center font-bold py-1 sm:py-2 ${theme.textMuted} text-sm sm:text-base lg:text-lg`}
          >
            <span className="hidden sm:inline">{day}</span>
            <span className="sm:hidden">{day.substring(0, 1)}</span>
          </div>
        ))}
      </div>

      {/* Calendar grid - scrollable */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden scrollbar-thin min-h-0" style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}>
        <div className="grid grid-cols-7 gap-1 sm:gap-2">
          {calendarDays.map((date, index) => {
            const dateEvents = getEventsForDate(date);
            const isDateToday = isToday(date.date);
            const isInCurrentMonth = isCurrentMonth(date.date, year, month);
            const hasEvents = dateEvents.length > 0;

            return (
              <div
                key={`${date.year}-${date.month}-${date.day}-${index}`}
                className={`${hasEvents ? 'min-h-[80px] sm:min-h-[100px] lg:min-h-[140px]' : 'min-h-[40px] sm:min-h-[50px] lg:min-h-[60px]'} p-0.5 sm:p-1 rounded-landing border-2 transition-all active:scale-95 cursor-pointer touch-manipulation relative ${
                  isDateToday
                    ? `${theme.today} font-semibold`
                    : hasEvents
                    ? `${theme.hasEvent} ${theme.border}`
                    : isInCurrentMonth
                    ? `${theme.bg} ${theme.border} ${theme.text}`
                    : `${theme.otherMonth} ${theme.border}`
                }`}
                onClick={() => onDateClick?.(date.date)}
              >
                {/* Day number overlay in top right corner */}
                <div className={`absolute top-0.5 sm:top-1 right-0.5 sm:right-1 text-xs sm:text-sm font-bold z-10 px-1 py-0.5 rounded-landing shadow-sm ${
                  isDateToday 
                    ? (isDarkMode ? 'bg-[rgba(96,165,250,0.2)] text-[#60a5fa]' : 'bg-[rgba(217,119,111,0.2)] text-[#d9776f]')
                    : `${theme.bg} ${theme.text}`
                }`}>
                  <span className="flex flex-col items-center leading-tight">
                    <span className="text-[8px] sm:text-[9px] font-normal opacity-75">
                      {date.date.toLocaleDateString('en-US', { month: 'short' })}
                    </span>
                    <span>{date.day}</span>
                  </span>
                </div>
                <div className="space-y-1 sm:space-y-2">
                  {(() => {
                    // Count past events (they're smaller, so we can fit more)
                    const pastEventsCount = dateEvents.filter(e => isEventPast(e)).length;
                    const hasPastEvents = pastEventsCount > 0;
                    
                    // Determine max visible events:
                    // Mobile: 2-3 events (3 if past events exist)
                    // Tablet/Desktop: 3-4 events (4 if past events exist)
                    const maxVisibleMobile = hasPastEvents ? 3 : 2;
                    const maxVisibleTablet = hasPastEvents ? 4 : 3;
                    
                    // Show events up to maxVisibleTablet, but hide some on mobile
                    const allVisibleEvents = dateEvents.slice(0, maxVisibleTablet);
                    const mobileVisibleEvents = dateEvents.slice(0, maxVisibleMobile);
                    const remainingCount = dateEvents.length - maxVisibleTablet;
                    
                    return (
                      <>
                        {/* Mobile: Show fewer events */}
                        {mobileVisibleEvents.map((event, eventIndex) => {
                          const eventIsPast = isEventPast(event);
                          const prevEvent = eventIndex > 0 ? mobileVisibleEvents[eventIndex - 1] : null;
                          const prevEventIsPast = prevEvent ? isEventPast(prevEvent) : false;
                          const reducedSpacing = (eventIsPast || prevEventIsPast) && eventIndex > 0;
                          return (
                            <div
                              key={event.id}
                              className={`sm:hidden ${eventIsPast ? 'opacity-70' : ''} ${reducedSpacing ? '-mt-0.5' : ''}`}
                            >
                              <EventCard
                                event={event}
                                onEventClick={onEventClick}
                                isDarkMode={isDarkMode}
                                size={eventIsPast ? 'sm' : 'md'}
                                isPast={eventIsPast}
                              />
                            </div>
                          );
                        })}
                        
                        {/* Tablet/Desktop: Show more events */}
                        {allVisibleEvents.map((event, eventIndex) => {
                          const eventIsPast = isEventPast(event);
                          const prevEvent = eventIndex > 0 ? allVisibleEvents[eventIndex - 1] : null;
                          const prevEventIsPast = prevEvent ? isEventPast(prevEvent) : false;
                          const reducedSpacing = (eventIsPast || prevEventIsPast) && eventIndex > 0;
                          return (
                            <div
                              key={event.id}
                              className={`hidden sm:block ${eventIsPast ? 'opacity-70' : ''} ${reducedSpacing ? '-mt-0.5 sm:-mt-1' : ''}`}
                            >
                              <EventCard
                                event={event}
                                onEventClick={onEventClick}
                                isDarkMode={isDarkMode}
                                size={eventIsPast ? 'sm' : 'md'}
                                isPast={eventIsPast}
                              />
                            </div>
                          );
                        })}
                        
                        {/* Show remaining count if there are more events */}
                        {remainingCount > 0 && (
                          <div 
                            className={`text-xs sm:text-sm lg:text-base ${theme.textMuted} font-bold py-0.5 sm:py-1 cursor-pointer hover:opacity-80 transition-opacity active:scale-95 touch-manipulation`}
                            onClick={(e) => {
                              e.stopPropagation();
                              onNavigateToDay?.(date.date);
                            }}
                          >
                            +{remainingCount} more
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

